import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { useAuthStore } from '../stores/authStore'
import { codexAPI } from '../services/codex'

export default function CodexAuditPage() {
  const { user, isImpersonating } = useAuthStore()
  if (user?.role !== 'super_admin' || isImpersonating) return <p className="p-6">Only super admins can read Codex audit logs.</p>
  return <AuditLog />
}

function AuditLog() {
  const [page, setPage] = useState({ events: [], nextCursor: null })
  const [filters, setFilters] = useState({ userId: '', sessionId: '', event: '' })
  const [query, setQuery] = useState({})
  const [details, setDetails] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    setDetails({})
    codexAPI.audit(query).then(({ data }) => { if (active) setPage(data) })
      .catch(err => { if (active) setError(err.response?.data?.error?.message || 'Unable to load audit events.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [query])

  async function inspect(eventId) {
    try {
      const { data } = await codexAPI.auditDetail(eventId)
      setDetails(current => ({ ...current, [eventId]: data }))
    } catch (err) { setError(err.response?.data?.error?.message || 'Unable to load event details.') }
  }

  return <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
    <Link to="/admin/codex" className="text-sm underline">Back to Codex</Link>
    <h1 className="text-2xl font-semibold">Codex audit log</h1>
    <p className="text-sm text-muted-foreground">Prompts, replies, tool activity, approvals, and session events. Credentials are redacted; records may contain clinic and customer data.</p>
    <form className="flex flex-wrap items-end gap-3" onSubmit={event => { event.preventDefault(); setQuery({ ...filters }) }}>
      {Object.entries({ userId: 'Core user ID', sessionId: 'Session ID', event: 'Event type' }).map(([key, label]) => <label key={key} className="text-sm space-y-1">
        <span className="block">{label}</span><input className="border rounded bg-background p-2" value={filters[key]} onChange={event => setFilters(current => ({ ...current, [key]: event.target.value }))} />
      </label>)}
      <Button disabled={loading}>Filter / refresh</Button>
    </form>
    {error && <p role="alert" className="text-destructive">{error}</p>}
    {loading ? <p role="status">Loading audit events…</p> : !page.events.length ? <p>No matching events.</p> : <div className="space-y-3">
      {page.events.map(event => <article key={event.eventId} className="border rounded-lg p-4 space-y-2">
        <div className="flex flex-wrap justify-between gap-2"><h2 className="font-medium break-all">{event.event}</h2><time className="text-sm text-muted-foreground">{new Date(event.capturedAt).toLocaleString()}</time></div>
        <p className="text-sm">{event.email || event.userId} · {event.role} · {event.clinicId || 'All accessible clinics'}</p>
        <p className="text-xs text-muted-foreground break-all">Session {event.sessionId} · Chat {event.chatId} · Event #{event.sequence}</p>
        <Button variant="outline" size="sm" onClick={() => inspect(event.eventId)}>View details</Button>
        {details[event.eventId] && (details[event.eventId].incomplete ? <p role="alert">This event was only partially saved.</p> : <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-3 text-xs">{JSON.stringify(details[event.eventId].details, null, 2)}</pre>)}
      </article>)}
    </div>}
    <div className="flex gap-2"><Button variant="outline" disabled={loading} onClick={() => setQuery({ ...filters })}>Latest events</Button><Button variant="outline" disabled={loading || !page.nextCursor} onClick={() => setQuery(current => ({ ...current, cursor: page.nextCursor }))}>Older events</Button></div>
  </div>
}
